import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../services/supabaseClient";
import { SubscriptionPlan } from "../types";

interface MerchantAuthProps {
  onAuthSuccess: (
    isNewUser: boolean,
    selectedPlan: SubscriptionPlan,
    credentials?: { email: string; venueName?: string; venueId?: string }
  ) => void;
  initialLoginMode?: boolean;
}

const MerchantAuth: React.FC<MerchantAuthProps> = ({
  onAuthSuccess,
  initialLoginMode = false,
}) => {
  const [isLogin, setIsLogin] = useState(initialLoginMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [venueName, setVenueName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLogin(initialLoginMode);
  }, [initialLoginMode]);

    useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsLogin(true);
        setIsRecoveringPassword(true);
        setErrorMsg(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const tokenSource = `${window.location.hash}${window.location.search}`;
    if (tokenSource.includes('type=recovery') || tokenSource.includes('access_token=')) {
      setIsLogin(true);
      setIsRecoveringPassword(true);
      setErrorMsg(null);
    }
  }, []);

  const previewQueryParam = new URLSearchParams(window.location.hash.split('?')[1] || '').get('previewVenue');
  const normalizedPreviewVenue = previewQueryParam?.trim() || '';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg(null);

    // ✅ define these FIRST (prevents "before initialization" errors)
    const ue = email.trim().toLowerCase();
    const vn = venueName.trim();

    try {
      // ==========================
      // LOGIN FLOW
      // ==========================
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: ue,
          password,
        });
        if (error) throw error;

        const userEmail = data.user?.email || ue;
        onAuthSuccess(false, "partner", { email: userEmail });
        return;
      }

      // ==========================
      // SIGNUP FLOW → CREATE VENUE → STRIPE CHECKOUT
      // ==========================

      if (!vn) throw new Error("Please enter your venue name.");
      if (!ue) throw new Error("Please enter a valid email.");

      // 1) Create Supabase auth user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: ue,
        password,
      });

      // If they try an email already registered, show the real message cleanly
      if (signUpError) throw signUpError;

      const userId = signUpData.user?.id;
      if (!userId) throw new Error("No user returned from signup.");

      // Session may be null when email confirmations are enabled.
      const accessToken = signUpData.session?.access_token ?? null;

      // 2) Create Venues row NOW (so we have a real Venues.id UUID)
      const venueRes = await fetch("/.netlify/functions/create-venue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // ✅ MUST match your Venues columns / what the function expects
          owner_user_id: userId,
          business_email: ue,
          venue_name: vn,
        }),
      });

      const venuePayload = await venueRes.json().catch(() => ({}));

      if (!venueRes.ok) {
        throw new Error(venuePayload?.error || "Failed to create venue row.");
      }

      // Expect handler returns { data: { id: 'uuid...' } } OR { id: 'uuid...' }
      const realVenueId =
        venuePayload?.data?.id || venuePayload?.id || venuePayload?.venue_id;

      if (!realVenueId) throw new Error("Venue created but no id returned.");

      // 3) Store locally so profile can pre-fill + app can route immediately
      localStorage.setItem("pendingVenueName", vn);
      localStorage.setItem("pendingVenueId", String(realVenueId));

      // 4) Start Stripe Checkout
      const checkoutHeaders: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        checkoutHeaders.Authorization = `Bearer ${accessToken}`;
      }
      
      const res = await fetch("/.netlify/functions/create-checkout-session", {
        method: "POST",
        headers: checkoutHeaders,
        body: JSON.stringify({
          venueId: realVenueId,   // ✅ use real Venues.id
          userId,
          email: ue,
          venueName: vn,
        }),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          payload?.error ||
          payload?.message ||
          (typeof payload === "string" ? payload : null) ||
          "Failed to start checkout.";
        throw new Error(msg);
      }

      if (!payload?.url) throw new Error("No checkout URL returned.");

      window.location.href = payload.url;
      return;
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.message ||
        err?.error_description ||
        "Could not start checkout. Please try again.";
      setErrorMsg(msg);
      alert(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDemoLogin = () => {
    onAuthSuccess(false, "partner", { email: "partner@demo.com" });
  };

  const handlePasswordResetRequest = async () => {
    const ue = email.trim().toLowerCase();
    if (!ue) {
      setErrorMsg("Enter your account email first, then click Forgot Password.");
      return;
    }

    setIsSaving(true);
    setErrorMsg(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(ue, {
        redirectTo: `${window.location.origin}/merchant-auth?mode=login`,
      });

      if (error) throw error;

      alert("Password reset email sent. Check your inbox for the secure link.");
    } catch (err: any) {
      const msg = err?.message || "Unable to send password reset email.";
      setErrorMsg(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!newPassword || newPassword.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setIsRecoveringPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      alert("Password updated. You can now access your dashboard.");

      onAuthSuccess(false, "partner", { email: data.user?.email || email.trim().toLowerCase() });
    } catch (err: any) {
      const msg = err?.message || "Unable to update password right now.";
      setErrorMsg(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const FEATURES = [
    "List All Your Happy Hours",
    "Smart AI Menu Scanner (Post in Seconds)",
    "Instant 'Flash Boost' Notifications",
    "Verified Partner Network Badge",
    "Priority Local Search Placement",
    "Customizable Venue Brand Profile",
    "Manage Specials in Real-Time",
  ];

  if (isLogin) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center pt-24 pb-20 px-6">
        <div className="w-full max-w-lg animate-in fade-in slide-in-from-bottom-8 duration-700">
          <div className="bg-white p-8 md:p-14 rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy relative text-left">
            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6 shadow-inner">
                🔑
              </div>
              <h2 className="text-3xl md:text-4xl font-black serif tracking-tight mb-2">
                Partner Login
              </h2>
              <p className="text-gray-400 font-medium text-sm">
                Access your venue dashboard.
              </p>
            </div>

            {errorMsg && (
              <div className="mb-6 p-4 rounded-2xl bg-red-50 text-red-700 text-sm font-bold">
                {errorMsg}
              </div>
            )}

            {isRecoveringPassword ? (
              <form onSubmit={handlePasswordRecoverySubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="At least 8 characters"
                    className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 focus:bg-white rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Re-enter your new password"
                    className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 focus:bg-white rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSaving}
                  className={`w-full h-16 bg-black text-white hover:bg-amber-500 hover:text-black font-black text-lg rounded-2xl transition-all active:scale-95 shadow-xl mt-4 ${
                    isSaving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {isSaving ? "UPDATING..." : "SET NEW PASSWORD"}
                </button>
              </form>
            ) : (

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="e.g. manager@venue.com"
                  className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 focus:bg-white rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 focus:bg-white rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                />
                <div className="pt-2 text-right">
                  <button
                    type="button"
                    onClick={handlePasswordResetRequest}
                    disabled={isSaving}
                    className="text-[10px] font-black text-gray-500 hover:text-amber-600 transition-colors uppercase tracking-[0.2em]"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className={`w-full h-16 bg-black text-white hover:bg-amber-500 hover:text-black font-black text-lg rounded-2xl transition-all active:scale-95 shadow-xl mt-4 ${
                  isSaving ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isSaving ? "ACCESSING..." : "ACCESS DASHBOARD"}
              </button>

              <div className="relative py-6 flex items-center gap-4">
                <div className="h-px bg-black/5 flex-1" />
                <span className="text-[8px] font-black text-gray-300 uppercase tracking-widest">
                  OR
                </span>
                <div className="h-px bg-black/5 flex-1" />
              </div>

              <button
                type="button"
                onClick={handleDemoLogin}
                className="w-full py-4 bg-gray-50 text-gray-400 font-black text-[10px] rounded-xl border border-black/5 uppercase tracking-widest hover:bg-amber-500 hover:text-white transition-all"
              >
                Launch Pre-filled Demo
              </button>

              <div className="pt-8 border-t border-black/5 mt-8 text-center space-y-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Don't have a venue listed yet?
                </p>
                <button
                  type="button"
                  onClick={() => setIsLogin(false)}
                  className="text-[11px] font-black text-amber-600 hover:text-black transition-colors uppercase tracking-[0.2em] underline underline-offset-4"
                >
                  Join the Collective
                </button>
              </div>
            </form>
             )}
          </div>
        </div>
      </div>
    );
  }

  // Signup view (your existing layout kept)
  return (
    <div className="min-h-screen bg-[#FFFDF5] pt-32 md:pt-48 pb-24 md:pb-40 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 md:mb-32 animate-in fade-in slide-in-from-top-10 duration-700">
          <h2 className="text-4xl sm:text-7xl md:text-9xl font-black tracking-tighter serif leading-none mb-6 md:mb-10">
            List your <span className="text-amber-600 italic">specials.</span>
          </h2>
          <p className="text-base md:text-2xl text-gray-500 font-medium max-w-4xl mx-auto leading-relaxed px-4">
            Reach thousands of local explorers looking for their next favorite spot.
            Join our verified partner network and grow your happy hour crowd.
          </p>
          <p className="mt-5 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-700 font-black text-[10px] md:text-xs uppercase tracking-[0.2em]">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            Start your 30-day free trial today.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-32">
          <div className="bg-black text-white p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] shadow-2xl border-2 border-amber-500 relative grainy overflow-hidden text-left">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="bg-amber-500 text-black font-black text-[10px] px-4 py-2 rounded-full uppercase tracking-widest mb-6 inline-block">
                  The All-In-One Plan
                </span>
                <h3 className="text-4xl md:text-6xl font-black serif tracking-tight mb-4">
                  Partner <br />
                  <span className="italic text-amber-500">Growth.</span>
                </h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl md:text-7xl font-black text-amber-500">
                    $25
                  </span>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-sm">
                    / month
                  </span>
                </div>
                <div className="text-amber-400 font-black text-[10px] uppercase tracking-widest mb-8 space-y-1">
                  <p>30-Day Free Trial</p>
                  <p>No Payment Due Today</p>
                  <p>Cancel Anytime</p>
                </div>
                <button
                  onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                  className="w-full md:w-auto px-10 py-5 bg-white text-black font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-amber-500 transition-all shadow-xl active:scale-95 text-center"
                >
                  Start My Free 30 Days
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-amber-500 font-black text-[10px] uppercase tracking-widest mb-4">
                  Included Features:
                </p>
                <ul className="space-y-4">
                  {FEATURES.map((feat, i) => (
                    <li
                      key={i}
                      className="flex gap-4 items-start text-[11px] font-bold uppercase tracking-wider"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-amber-500 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-white/80">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <section
          id="registration"
          ref={formRef}
          className="max-w-3xl mx-auto animate-in fade-in duration-1000 text-left"
        >
          <div className="bg-white p-8 md:p-16 rounded-[2.5rem] md:rounded-[3rem] shadow-2xl border-2 border-black/[0.03] grainy relative">
            <h3 className="text-3xl md:text-5xl font-bold serif mb-6 md:mb-7 tracking-tight leading-tight text-center">
              Claim your <span className="text-amber-600 italic">spot.</span>
            </h3>

            {errorMsg && (
              <div className="mb-6 p-4 rounded-2xl bg-red-50 text-red-700 text-sm font-bold">
                {errorMsg}
              </div>
            )}

            {normalizedPreviewVenue && (
              <div className="mx-auto mb-6 max-w-2xl rounded-[1.5rem] border border-amber-500/20 bg-amber-50/80 px-4 py-4 md:px-5 md:py-4.5 text-left shadow-[0_8px_24px_rgba(245,158,11,0.08)]">
                <p className="text-[11px] font-semibold tracking-[0.06em] text-amber-700/85">
                  You’re viewing a preview listing for {normalizedPreviewVenue}
                </p>
                <p className="mt-2 text-[13px] md:text-sm font-medium leading-[1.45] text-gray-700">
                  Preview listings are temporary launch listings created from publicly available information. Once your venue is registered and verified, the preview listing will be removed so you can take full control and manage your own specials.
                </p>
                <p className="mt-2 text-[13px] md:text-sm font-medium leading-[1.45] text-gray-700">
                  Want your preview specials carried over?{' '}
                  <a
                    href="mailto:hello@hoppy-hours.com"
                    className="font-black text-amber-700 underline underline-offset-4 hover:text-black transition-colors"
                  >
                    Contact hello@hoppy-hours.com.
                  </a>
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-5">
                    Venue Name
                  </label>
                  <input
                    required
                    type="text"
                    value={venueName}
                    onChange={(e) => setVenueName(e.target.value)}
                    placeholder="e.g. The Rusty Anchor"
                    className="w-full h-14 md:h-18 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-5">
                    Business Email
                  </label>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="manager@venue.com"
                    className="w-full h-14 md:h-18 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-5">
                  Create Password
                </label>
                <input
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-14 md:h-18 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`w-full py-6 md:h-24 bg-black text-white hover:bg-amber-500 hover:text-black font-black text-xl md:text-2xl rounded-2xl md:rounded-3xl transition-all active:scale-[0.98] shadow-2xl flex flex-col items-center justify-center gap-1 text-center ${
                    isSaving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <span className="uppercase tracking-[0.1em] text-sm md:text-xl">
                    {isSaving ? "STARTING CHECKOUT..." : "CREATE MY ACCOUNT"}
                  </span>
                  <span className="text-[8px] md:text-[10px] font-bold opacity-60 uppercase tracking-widest">
                    Next: secure checkout
                  </span>
                </button>
              </div>

              <div className="pt-8 border-t border-black/5 mt-8 text-center space-y-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Already a partner?
                </p>
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  className="text-[11px] md:text-sm font-black text-black hover:text-amber-600 transition-colors uppercase tracking-[0.3em] underline underline-offset-8 decoration-2"
                >
                  Log in to Dashboard
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MerchantAuth;
